import { getIcon, showToast } from '../components/ui.js';

const GITHUB_REPO_URL = 'https://github.com/martinbat89/business-knowledge';
const GITHUB_WIKI_URL = 'https://github.com/martinbat89/business-knowledge/tree/main/Wiki';

const DOCS_DATA = [
  {
    id: 'tarifas',
    type: 'PricingModel',
    category: 'Ventas & Tarifas',
    title: 'Estructura de Tarifas, Costos y Comisiones',
    file: 'sales/estructura-tarifas-y-comisiones.md',
    desc: 'Modelo de fijación de precios y cálculo de valor hora por canal comercial para trasladar comisiones e impuestos al cliente.',
    tags: ['tarifas', 'finanzas', 'fiverr', 'costos', 'comisiones'],
    version: '1.0',
    contentHtml: `
      <div class="space-y-3 text-xs leading-relaxed text-text-primary">
        <p class="font-medium text-sm text-text-primary">Principio Financiero Fundamental:</p>
        <p class="text-text-secondary">El negocio calcula el valor hora base en moneda local o divisa neta y <b>traslada íntegramente las comisiones bancarias, de pasarelas y plataformas al cliente</b>, evitando que los costos de intermediación erosionen la rentabilidad.</p>
        
        <div class="border border-border rounded p-3 bg-surface-subtle space-y-2 font-mono text-[11px]">
          <div><b>1. Costo Directo Local:</b> $9.96 USD / $15.931 ARS/h. Comisión: <b>0%</b>. Transferencia directa / Mercado Pago.</div>
          <div><b>2. Exterior Directo:</b> $10.47 USD/h. Comisión estimada: <b>~5%</b> (Payoneer 2% + Takenos 3%). Traslado al cliente.</div>
          <div><b>3. Fiverr Internacional:</b> $16.59 USD/h. Retención acumulada: <b>~24%</b> (Fiverr 20% + retiro). Tarifa bruta adaptada.</div>
        </div>

        <p class="font-medium text-sm text-text-primary pt-1">Modalidades de Proyecto:</p>
        <ul class="list-disc pl-4 space-y-1 text-text-secondary">
          <li><b>Presupuesto Cerrado (Fixed Budget):</b> Horas estimadas fijas por tarea. Seguimiento de desvíos en tiempo real.</li>
          <li><b>Por Horas Reales (Time & Materials):</b> Liquidación periódica según las horas numéricas efectivamente imputadas.</li>
        </ul>
      </div>
    `
  },
  {
    id: 'diagnostico',
    type: 'Methodology',
    category: 'Metodología',
    title: 'Proceso de Diagnóstico y Propuesta',
    file: 'methodology/diagnostico-y-propuesta.md',
    desc: 'Metodología estándar de 4 fases para evaluar la viabilidad de un proyecto tecnológico y emitir una propuesta comercial sólida.',
    tags: ['methodology', 'diagnostico', 'propuesta', 'ventas'],
    version: '1.0',
    contentHtml: `
      <div class="space-y-3 text-xs leading-relaxed text-text-primary">
        <p class="text-text-secondary">Metodología de 4 pasos para estructurar la preventa y cotización sin fugas de alcance:</p>
        <ol class="list-decimal pl-4 space-y-2 text-text-secondary">
          <li><b>Relevamiento y Descubrimiento:</b> Escuchar el dolor operacional del cliente (fricción en planillas, falta de visibilidad, procesos manuales repetitivos). Identificar sistemas y APIs actuales.</li>
          <li><b>Definición del Alcance (Scope):</b> Desglosar el requerimiento en tareas atómicas estimables. Mapear riesgos técnicos e integraciones.</li>
          <li><b>Estimación de Horas y Tarifa:</b> Asignar horas estimadas por tarea, definir canal de cobro y determinar si es Presupuesto Fijo o Horas Reales.</li>
          <li><b>Presentación y Cierre:</b> Enviar propuesta con hitos claros y fechas estimadas. Mantener en <i>Propuesta en curso</i> y luego resolver a <i>Ganado / Activo</i> o <i>Descartado</i> según la respuesta.</li>
        </ol>
      </div>
    `
  },
  {
    id: 'wbs',
    type: 'Methodology',
    category: 'Metodología',
    title: 'Gestión y Estructuración de Proyectos (WBS)',
    file: 'methodology/gestion-proyectos-alerce.md',
    desc: 'Criterios de organización en 4 niveles jerárquicos (Cliente -> Proyecto -> Tarea -> Subtarea) y control de desvíos.',
    tags: ['methodology', 'proyectos', 'wbs', 'seguimiento'],
    version: '1.0',
    contentHtml: `
      <div class="space-y-3 text-xs leading-relaxed text-text-primary">
        <p class="text-text-secondary">Estructura jerárquica de Work Breakdown Structure (WBS) para todo servicio de Equipo Alerce:</p>
        <div class="space-y-2 border border-border p-3 rounded bg-surface-subtle font-mono text-[11px]">
          <div><b>1. Cliente:</b> Entidad comercial que contrata los servicios.</div>
          <div><b>2. Proyecto:</b> Contenedor de un objetivo específico (Fixed Budget o Time & Materials).</div>
          <div><b>3. Tarea:</b> Unidad de planificación presupuestada con plazo límite y estimación de horas.</div>
          <div><b>4. Subtarea:</b> Registro numérico periódico donde se imputan horas reales y bitácora de actividad.</div>
        </div>
        <p class="font-medium text-sm text-text-primary pt-1">Monitoreo de Eficiencia:</p>
        <p class="text-text-secondary">Fórmula de Desvío: <code>(Horas Reales - Horas Estimadas) / Horas Estimadas * 100</code>.<br/>Desvío &lt; 0% = Mayor rentabilidad. Desvío &gt; 0% = Alerta de sobrecosto o redefinición de alcance.</p>
      </div>
    `
  },
  {
    id: 'imputacion',
    type: 'Procedure',
    category: 'Procedimientos',
    title: 'Imputación de Horas y Carga de Subtareas',
    file: 'procedures/imputacion-horas-y-subtareas.md',
    desc: 'Procedimiento estándar para asentar periódicamente las horas trabajadas en el Business OS mediante input numérico o cronómetro.',
    tags: ['procedures', 'horas', 'timetracking', 'subtareas'],
    version: '1.0',
    contentHtml: `
      <div class="space-y-3 text-xs leading-relaxed text-text-primary">
        <p class="text-text-secondary">Guía paso a paso para el registro exacto de las horas reales invertidas en cada tarea:</p>
        <ol class="list-decimal pl-4 space-y-2 text-text-secondary">
          <li><b>Frecuencia:</b> El registro debe realizarse diariamente o al finalizar bloques de trabajo concentrado para mantener precisión.</li>
          <li><b>Seleccionar Tarea:</b> En <code>#/projects</code> seleccionar el proyecto y ubicar la tarea en curso.</li>
          <li><b>Carga Numérica Decimal:</b> Ingresar el valor numérico exacto (ej. <code>0.25</code> para 15m, <code>0.50</code> para 30m, <code>1.25</code> para 1h 15m). O utilizar el cronómetro digital interactivo.</li>
          <li><b>Bitácora:</b> Registrar brevemente qué se resolvió para respaldo ante el cliente o auditoría interna.</li>
        </ol>
      </div>
    `
  },
  {
    id: 'cliente',
    type: 'Procedure',
    category: 'Procedimientos',
    title: 'Alta y Cualificación de Nuevo Cliente',
    file: 'procedures/alta-nuevo-cliente.md',
    desc: 'Procedimiento para incorporar un nuevo prospecto o cliente en el Business OS y clasificar su canal.',
    tags: ['procedures', 'clientes', 'onboarding', 'crm'],
    version: '1.0',
    contentHtml: `
      <div class="space-y-3 text-xs leading-relaxed text-text-primary">
        <p class="text-text-secondary">Flujo estándar de ingreso comercial:</p>
        <ol class="list-decimal pl-4 space-y-2 text-text-secondary">
          <li><b>Recepción del Contacto:</b> Identificar canal de origen (Fiverr, Redes, Recomendación directa).</li>
          <li><b>Carga en el Pipeline Comercial:</b> Asentar Nombre, Canal, País, Empresa y Redes sociales en <code>#/pipeline</code>.</li>
          <li><b>Apertura de Oportunidad:</b> Describir la necesidad inicial y el valor tentativo estimado.</li>
          <li><b>Seguimiento:</b> Fijar fecha de contacto para reunión de diagnóstico o envío de demo/propuesta.</li>
        </ol>
      </div>
    `
  }
];

export async function renderKnowledgeView() {
  return `
    <div class="space-y-6 animate-fade-in">
      <!-- Top Action Bar -->
      <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 pb-4 border-b border-border">
        <div>
          <div class="flex items-center gap-2.5 flex-wrap">
            <h1 class="text-2xl font-bold tracking-tight text-text-primary">Knowledge OS & OpenKnowledge</h1>
            <span class="inline-flex items-center gap-1 text-[11px] font-mono px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200">
              ${getIcon('check', 'w-3 h-3 text-emerald-600')}
              OKF v0.2 Validado (10/10 Checks)
            </span>
          </div>
          <p class="text-xs text-text-secondary mt-1">
            Base de conocimiento metodológico, operativo y técnico de Equipo Alerce estructurada en Markdown estándar
          </p>
        </div>

        <!-- Direct Action Buttons -->
        <div class="flex items-center gap-2.5 flex-wrap">
          <button 
            id="btn-open-help-modal" 
            class="h-9 inline-flex items-center justify-center gap-2 px-3.5 text-xs font-semibold rounded-md border border-border bg-surface text-text-primary hover:bg-surface-subtle transition-all shadow-sm cursor-pointer whitespace-nowrap box-border leading-none"
          >
            ${getIcon('helpCircle', 'w-4 h-4 text-primary shrink-0')}
            <span>Guía de Uso & Ayuda</span>
          </button>

          <a 
            href="${GITHUB_WIKI_URL}" 
            target="_blank" 
            rel="noopener noreferrer"
            class="h-9 inline-flex items-center justify-center gap-2 px-3.5 text-xs font-semibold rounded-md border border-border bg-surface text-text-primary hover:bg-surface-subtle transition-all shadow-sm cursor-pointer whitespace-nowrap box-border leading-none"
          >
            ${getIcon('book', 'w-4 h-4 text-text-secondary shrink-0')}
            <span>Explorar Wiki/ en GitHub</span>
            ${getIcon('externalLink', 'w-3 h-3 text-text-secondary shrink-0')}
          </a>

          <a 
            href="${GITHUB_REPO_URL}" 
            target="_blank" 
            rel="noopener noreferrer"
            class="h-9 inline-flex items-center justify-center gap-2 px-3.5 text-xs font-semibold rounded-md border border-transparent bg-primary text-white hover:bg-primary-hover active:bg-primary-active transition-all shadow-sm cursor-pointer whitespace-nowrap box-border leading-none"
          >
            ${getIcon('briefcase', 'w-4 h-4 text-white shrink-0')}
            <span>Repositorio Privado en GitHub</span>
            ${getIcon('externalLink', 'w-3 h-3 text-white shrink-0')}
          </a>
        </div>
      </div>

      <!-- Architecture Clarification Banner -->
      <div class="bg-surface p-5 rounded-md border border-border shadow-card space-y-3">
        <div class="flex items-center gap-2 text-text-primary font-bold text-sm">
          ${getIcon('book', 'w-4 h-4 text-primary')}
          <span>Arquitectura Desacoplada: ¿Por qué tu conocimiento está aquí y no en una base de datos?</span>
        </div>
        <p class="text-xs text-text-secondary leading-relaxed">
          El <b>Business OS</b> administra tus datos transaccionales diarios (clientes, proyectos, tareas, facturas) en Supabase. En cambio, el <b>Knowledge OS</b> preserva tu <b>know-how estratégico y metodológico</b> en archivos Markdown estándar bajo la especificación <b>Open Knowledge Format (OKF v0.2)</b> en el repositorio independiente <code>business-knowledge/</code>.
        </p>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 text-xs">
          <div class="p-3 bg-surface-subtle rounded border border-border/80 space-y-1">
            <span class="font-bold text-text-primary flex items-center gap-1.5">
              ${getIcon('check', 'w-3.5 h-3.5 text-emerald-600')} 1. 100% Tuyo y Portable
            </span>
            <p class="text-text-secondary text-[11px] leading-relaxed">Nunca estás atado a una plataforma. Si mañana cambias de software, tus notas y procesos siguen intactos en Markdown.</p>
          </div>
          <div class="p-3 bg-surface-subtle rounded border border-border/80 space-y-1">
            <span class="font-bold text-text-primary flex items-center gap-1.5">
              ${getIcon('check', 'w-3.5 h-3.5 text-emerald-600')} 2. Versionado en Git
            </span>
            <p class="text-text-secondary text-[11px] leading-relaxed">Cada cambio tiene autor, fecha y diff histórico en tu cuenta privada de GitHub mediante commits limpios.</p>
          </div>
          <div class="p-3 bg-surface-subtle rounded border border-border/80 space-y-1">
            <span class="font-bold text-text-primary flex items-center gap-1.5">
              ${getIcon('check', 'w-3.5 h-3.5 text-emerald-600')} 3. Listo para IA
            </span>
            <p class="text-text-secondary text-[11px] leading-relaxed">Cualquier agente de IA (Antigravity, Cursor, Claude) puede leer directamente la carpeta y redactar presupuestos con tu metodología.</p>
          </div>
        </div>
      </div>

      <!-- Quick Access Hub -->
      <div class="space-y-3">
        <div class="flex items-center justify-between">
          <h2 class="text-xs font-bold uppercase tracking-wider text-text-secondary">Las 4 Vías de Acceso a tu Conocimiento</h2>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
          <div class="p-4 bg-surface rounded-md border border-border shadow-card flex flex-col justify-between space-y-2">
            <div>
              <span class="font-bold text-text-primary flex items-center gap-1.5 text-sm">
                ${getIcon('externalLink', 'w-4 h-4 text-primary')} GitHub Web
              </span>
              <p class="text-text-secondary text-[11px] mt-1 leading-relaxed">
                Navega y edita en la nube desde cualquier dispositivo. <i>Tip: presiona la tecla <b>.</b> (punto) en GitHub para abrir el editor web.</i>
              </p>
            </div>
            <a href="${GITHUB_REPO_URL}" target="_blank" class="text-xs font-semibold text-primary hover:underline flex items-center gap-1 pt-2 border-t border-border">
              Abrir en GitHub ${getIcon('arrowUpRight', 'w-3 h-3')}
            </a>
          </div>

          <div class="p-4 bg-surface rounded-md border border-border shadow-card flex flex-col justify-between space-y-2">
            <div>
              <span class="font-bold text-text-primary flex items-center gap-1.5 text-sm">
                ${getIcon('briefcase', 'w-4 h-4 text-primary')} Obsidian / VS Code
              </span>
              <p class="text-text-secondary text-[11px] mt-1 leading-relaxed">
                Abre la carpeta <code>business-knowledge/</code> localmente como Vault en Obsidian para ver el grafo visual interactivo de notas.
              </p>
            </div>
            <span class="text-[10px] font-mono text-text-secondary pt-2 border-t border-border">Ruta: ./business-knowledge/</span>
          </div>

          <div class="p-4 bg-surface rounded-md border border-border shadow-card flex flex-col justify-between space-y-2">
            <div>
              <span class="font-bold text-text-primary flex items-center gap-1.5 text-sm">
                ${getIcon('terminal', 'w-4 h-4 text-primary')} Lector Web Local
              </span>
              <p class="text-text-secondary text-[11px] mt-1 leading-relaxed">
                Corre <code>okn view Wiki</code> en la terminal y navega tu base de conocimiento visualmente como un sitio web en localhost.
              </p>
            </div>
            <button class="btn-copy-quick text-xs font-semibold text-primary hover:underline flex items-center gap-1 pt-2 border-t border-border text-left cursor-pointer" data-cmd=".\\bin\\okn.exe view Wiki">
              Copiar comando ${getIcon('copy', 'w-3 h-3')}
            </button>
          </div>

          <div class="p-4 bg-surface rounded-md border border-border shadow-card flex flex-col justify-between space-y-2">
            <div>
              <span class="font-bold text-text-primary flex items-center gap-1.5 text-sm">
                ${getIcon('settings', 'w-4 h-4 text-primary')} Agentes de IA
              </span>
              <p class="text-text-secondary text-[11px] mt-1 leading-relaxed">
                Pídele a tu IA: <i>"Consulta en la base de conocimiento el proceso de diagnóstico y redacta la propuesta para X"</i>.
              </p>
            </div>
            <span class="text-[10px] font-mono text-emerald-700 font-semibold pt-2 border-t border-border">Directo en Antigravity</span>
          </div>
        </div>
      </div>

      <!-- Certified Documents Catalog with Actions -->
      <div class="space-y-3">
        <div class="flex items-center justify-between">
          <div>
            <h2 class="text-xs font-bold uppercase tracking-wider text-text-secondary">Conceptos y Metodologías Certificadas</h2>
            <p class="text-xs text-text-secondary mt-0.5">Haz clic en <b>Vista Previa</b> para leer el documento aquí mismo o en <b>Abrir en GitHub</b> para editarlo</p>
          </div>
          <span class="text-xs font-mono px-2 py-0.5 rounded bg-surface border border-border text-text-secondary">
            5 Documentos Clave
          </span>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          ${DOCS_DATA.map(doc => `
            <div class="bg-surface p-4 rounded-md border border-border shadow-card hover:border-zinc-400 transition-all flex flex-col justify-between space-y-3">
              <div class="space-y-2">
                <div class="flex items-center justify-between gap-2">
                  <span class="text-[10px] font-mono font-semibold px-2 py-0.5 rounded bg-surface-subtle border border-border text-text-secondary">
                    ${doc.category}
                  </span>
                  <span class="text-[10px] font-mono text-text-secondary">v${doc.version}</span>
                </div>
                <div>
                  <h3 class="font-bold text-sm text-text-primary tracking-tight">${doc.title}</h3>
                  <p class="text-xs text-text-secondary mt-1 leading-relaxed">${doc.desc}</p>
                </div>
                <div class="flex items-center gap-1 flex-wrap pt-1">
                  ${doc.tags.map(tag => `
                    <span class="text-[9px] font-mono px-1.5 py-0.5 rounded bg-surface-subtle text-text-secondary border border-border/60">
                      #${tag}
                    </span>
                  `).join('')}
                </div>
              </div>

              <div class="pt-3 border-t border-border/80 flex items-center justify-between gap-2">
                <button 
                  class="btn-preview-doc text-xs font-semibold px-2.5 py-1.5 rounded bg-surface-subtle hover:bg-surface border border-border text-text-primary transition-all flex items-center gap-1.5 shadow-sm cursor-pointer"
                  data-doc-id="${doc.id}"
                >
                  ${getIcon('book', 'w-3.5 h-3.5')}
                  <span>Vista Previa</span>
                </button>

                <a 
                  href="${GITHUB_REPO_URL}/blob/main/Wiki/${doc.file}" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  class="text-xs font-semibold text-text-secondary hover:text-text-primary flex items-center gap-1"
                >
                  <span>Abrir en GitHub</span>
                  ${getIcon('externalLink', 'w-3 h-3')}
                </a>
              </div>
            </div>
          `).join('')}
        </div>
      </div>

      <!-- OpenKnowledge CLI Commands Hub -->
      <div class="bg-primary text-white p-6 rounded-md shadow-card space-y-4">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div class="flex items-center gap-2">
            ${getIcon('terminal', 'w-5 h-5 text-zinc-300')}
            <h3 class="font-bold text-base tracking-tight text-white">
              Catálogo Completo de Comandos de OpenKnowledge CLI (Local)
            </h3>
          </div>
          <span class="text-xs font-mono text-zinc-300 bg-zinc-800 px-2.5 py-1 rounded border border-zinc-700">
            Binario nativo: ./bin/okn.exe (v0.7.1 Windows)
          </span>
        </div>

        <p class="text-xs text-zinc-300 leading-relaxed">
          Desde PowerShell en la carpeta <code>business-knowledge/</code> puedes ejecutar estas operaciones para auditar, navegar, buscar o exportar tu base de conocimiento:
        </p>

        <div class="space-y-2.5">
          <!-- Command 1: Validate -->
          <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-zinc-900/90 p-3 rounded border border-zinc-800 font-mono text-xs">
            <div class="space-y-0.5">
              <span class="text-zinc-500 block text-[10px] uppercase tracking-wider font-semibold">1. Auditoría y Validación de Estándar OKF v0.2:</span>
              <span class="text-emerald-400 font-bold">.\\bin\\okn.exe validate Wiki</span>
            </div>
            <button class="btn-copy-cmd self-start sm:self-center px-2.5 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs font-sans transition-all flex items-center gap-1 border border-zinc-700 cursor-pointer" data-cmd=".\\bin\\okn.exe validate Wiki">
              ${getIcon('copy', 'w-3 h-3')} Copiar
            </button>
          </div>

          <!-- Command 2: Web View Server -->
          <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-zinc-900/90 p-3 rounded border border-zinc-800 font-mono text-xs">
            <div class="space-y-0.5">
              <span class="text-zinc-500 block text-[10px] uppercase tracking-wider font-semibold">2. Levantar Lector Visual Web en Localhost:</span>
              <span class="text-emerald-400 font-bold">.\\bin\\okn.exe view Wiki</span>
            </div>
            <button class="btn-copy-cmd self-start sm:self-center px-2.5 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs font-sans transition-all flex items-center gap-1 border border-zinc-700 cursor-pointer" data-cmd=".\\bin\\okn.exe view Wiki">
              ${getIcon('copy', 'w-3 h-3')} Copiar
            </button>
          </div>

          <!-- Command 3: Search with Context -->
          <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-zinc-900/90 p-3 rounded border border-zinc-800 font-mono text-xs">
            <div class="space-y-0.5">
              <span class="text-zinc-500 block text-[10px] uppercase tracking-wider font-semibold">3. Búsqueda Contextual Inteligente:</span>
              <span class="text-emerald-400 font-bold">.\\bin\\okn.exe search Wiki "tarifas fiverr comisiones"</span>
            </div>
            <button class="btn-copy-cmd self-start sm:self-center px-2.5 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs font-sans transition-all flex items-center gap-1 border border-zinc-700 cursor-pointer" data-cmd=".\\bin\\okn.exe search Wiki &quot;tarifas fiverr comisiones&quot;">
              ${getIcon('copy', 'w-3 h-3')} Copiar
            </button>
          </div>

          <!-- Command 4: Inspect Tree List -->
          <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-zinc-900/90 p-3 rounded border border-zinc-800 font-mono text-xs">
            <div class="space-y-0.5">
              <span class="text-zinc-500 block text-[10px] uppercase tracking-wider font-semibold">4. Listar Estructura y Árbol de Conceptos:</span>
              <span class="text-emerald-400 font-bold">.\\bin\\okn.exe list Wiki</span>
            </div>
            <button class="btn-copy-cmd self-start sm:self-center px-2.5 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs font-sans transition-all flex items-center gap-1 border border-zinc-700 cursor-pointer" data-cmd=".\\bin\\okn.exe list Wiki">
              ${getIcon('copy', 'w-3 h-3')} Copiar
            </button>
          </div>

          <!-- Command 5: Export Static HTML -->
          <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-zinc-900/90 p-3 rounded border border-zinc-800 font-mono text-xs">
            <div class="space-y-0.5">
              <span class="text-zinc-500 block text-[10px] uppercase tracking-wider font-semibold">5. Exportar a Sitio Web HTML Estático:</span>
              <span class="text-emerald-400 font-bold">.\\bin\\okn.exe export html --out ./site Wiki</span>
            </div>
            <button class="btn-copy-cmd self-start sm:self-center px-2.5 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs font-sans transition-all flex items-center gap-1 border border-zinc-700 cursor-pointer" data-cmd=".\\bin\\okn.exe export html --out ./site Wiki">
              ${getIcon('copy', 'w-3 h-3')} Copiar
            </button>
          </div>

          <!-- Command 6: MCP AI Server -->
          <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-zinc-900/90 p-3 rounded border border-zinc-800 font-mono text-xs">
            <div class="space-y-0.5">
              <span class="text-zinc-500 block text-[10px] uppercase tracking-wider font-semibold">6. Servidor MCP para Clientes de IA (Claude, Cursor):</span>
              <span class="text-emerald-400 font-bold">.\\bin\\okn.exe mcp Wiki</span>
            </div>
            <button class="btn-copy-cmd self-start sm:self-center px-2.5 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs font-sans transition-all flex items-center gap-1 border border-zinc-700 cursor-pointer" data-cmd=".\\bin\\okn.exe mcp Wiki">
              ${getIcon('copy', 'w-3 h-3')} Copiar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL DE AYUDA Y GUÍA DE USO -->
    <div id="modal-help-knowledge" class="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 hidden">
      <div class="bg-surface rounded-md border border-border shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col overflow-hidden animate-fade-in">
        <div class="p-5 border-b border-border flex items-center justify-between bg-surface">
          <div class="flex items-center gap-2">
            ${getIcon('helpCircle', 'w-5 h-5 text-primary')}
            <h3 class="font-bold text-base text-text-primary">Guía Completa de Knowledge OS & OpenKnowledge</h3>
          </div>
          <button id="btn-close-help-modal" class="text-text-secondary hover:text-text-primary p-1 rounded hover:bg-surface-subtle cursor-pointer">
            ${getIcon('plus', 'w-5 h-5 rotate-45')}
          </button>
        </div>

        <div class="p-6 overflow-y-auto space-y-6 text-xs leading-relaxed text-text-primary">
          <!-- Section 1 -->
          <div class="space-y-2">
            <h4 class="font-bold text-sm text-text-primary flex items-center gap-1.5">
              1. ¿Por qué está separado del Business OS?
            </h4>
            <p class="text-text-secondary">
              El <b>Business OS</b> administra datos transaccionales en Supabase (clientes, tareas, facturación). El <b>Knowledge OS</b> guarda tu conocimiento procedimental y metodológico en archivos Markdown estructurados bajo el estándar internacional <b>Open Knowledge Format (OKF v0.2)</b>.
            </p>
            <p class="text-text-secondary">
              Al no estar en una base de datos relacional, <b>tu conocimiento nunca queda atrapado</b>: es portable, auditable por consola, editable sin conexión y compatible con cualquier IA o editor de Markdown del mercado.
            </p>
          </div>

          <!-- Section 2 -->
          <div class="space-y-2">
            <h4 class="font-bold text-sm text-text-primary flex items-center gap-1.5">
              2. Las Formas de Consultar y Leer tus Notas
            </h4>
            <ul class="list-disc pl-4 space-y-2 text-text-secondary">
              <li><b>En GitHub Web:</b> Entra a <a href="${GITHUB_REPO_URL}" target="_blank" class="text-primary font-medium hover:underline">github.com/martinbat89/business-knowledge</a>. <i>Tip Ninja:</i> Estando en la web de GitHub, presiona la tecla <b>.</b> (punto) en tu teclado físico para abrir el editor web de VS Code directamente en tu navegador.</li>
              <li><b>En Local con Obsidian o VS Code:</b> Abre la carpeta <code>business-knowledge/</code> en Obsidian para disfrutar del grafo interactivo de notas y búsqueda global instantánea.</li>
              <li><b>Lector Web Local (CLI):</b> Abre PowerShell en la carpeta y ejecuta <code>.\\bin\\okn.exe view Wiki</code> para navegar la documentación en tu navegador local.</li>
              <li><b>Con Agentes de IA:</b> Pídele a Antigravity: <i>"Consulta en la Wiki el template de propuestas y elabora una cotización para el cliente X"</i>.</li>
            </ul>
          </div>

          <!-- Section 3 -->
          <div class="space-y-2">
            <h4 class="font-bold text-sm text-text-primary flex items-center gap-1.5">
              3. ¿Cómo Agregar Nuevo Conocimiento? (Flujo Paso a Paso)
            </h4>
            <ol class="list-decimal pl-4 space-y-2 text-text-secondary">
              <li><b>Crear el archivo:</b> Crea un nuevo archivo <code>.md</code> en la subcarpeta correspondiente (ej: <code>Wiki/solutions/nuevo-bot-n8n.md</code> o <code>Wiki/procedures/nuevo-proceso.md</code>).</li>
              <li>
                <b>Agregar Cabecera Frontmatter YAML:</b> Coloca al principio del archivo:
                <pre class="bg-surface-subtle p-2.5 rounded border border-border font-mono text-[10px] text-text-primary mt-1">
---
type: Solution
title: Bot Automatizado de Consultas
description: Flujo n8n para responder cotizaciones frecuentes.
tags: [n8n, bots, automatizacion]
version: 1.0
---</pre>
              </li>
              <li><b>Vincular en el índice:</b> Agrega el link en el archivo <code>index.md</code> de esa carpeta (ej: <code>- [Bot de Consultas](nuevo-bot-n8n.md)</code>).</li>
              <li><b>Auditar con la CLI:</b> Ejecuta <code>.\\bin\\okn.exe validate Wiki</code> para confirmar que cumple 100% el estándar OKF.</li>
              <li><b>Respaldar:</b> Haz <code>git add .</code>, <code>git commit -m "docs: add bot n8n"</code> y <code>git push</code> para sincronizar con GitHub.</li>
            </ol>
          </div>
        </div>

        <div class="p-4 border-t border-border bg-surface-subtle flex justify-end gap-2">
          <button id="btn-close-help-modal-footer" class="px-4 py-2 text-xs font-semibold rounded bg-surface border border-border hover:bg-surface text-text-primary cursor-pointer">
            Cerrar Guía
          </button>
        </div>
      </div>
    </div>

    <!-- MODAL DE VISTA PREVIA DE DOCUMENTO -->
    <div id="modal-doc-preview" class="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 hidden">
      <div class="bg-surface rounded-md border border-border shadow-2xl max-w-xl w-full max-h-[85vh] flex flex-col overflow-hidden animate-fade-in">
        <div class="p-5 border-b border-border flex items-center justify-between bg-surface">
          <div class="space-y-1">
            <div class="flex items-center gap-2">
              <span id="preview-category" class="text-[10px] font-mono font-semibold px-2 py-0.5 rounded bg-surface-subtle border border-border text-text-secondary"></span>
              <span id="preview-version" class="text-[10px] font-mono text-text-secondary"></span>
            </div>
            <h3 id="preview-title" class="font-bold text-base text-text-primary tracking-tight"></h3>
          </div>
          <button id="btn-close-doc-preview" class="text-text-secondary hover:text-text-primary p-1 rounded hover:bg-surface-subtle cursor-pointer">
            ${getIcon('plus', 'w-5 h-5 rotate-45')}
          </button>
        </div>

        <div class="p-6 overflow-y-auto space-y-4">
          <div class="p-3 bg-surface-subtle rounded border border-border text-xs text-text-secondary italic" id="preview-desc"></div>
          <div id="preview-body" class="pt-2"></div>
        </div>

        <div class="p-4 border-t border-border bg-surface-subtle flex items-center justify-between gap-2">
          <a id="preview-github-link" href="#" target="_blank" rel="noopener noreferrer" class="text-xs font-semibold text-primary hover:underline flex items-center gap-1">
            Editar en GitHub ${getIcon('externalLink', 'w-3.5 h-3.5')}
          </a>
          <button id="btn-close-doc-preview-footer" class="px-4 py-1.5 text-xs font-semibold rounded bg-surface border border-border hover:bg-surface text-text-primary cursor-pointer">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  `;
}

export function initKnowledgeEvents() {
  // Help Modal Handlers
  const helpModal = document.getElementById('modal-help-knowledge');
  const openHelpBtn = document.getElementById('btn-open-help-modal');
  const closeHelpBtn = document.getElementById('btn-close-help-modal');
  const closeHelpBtnFooter = document.getElementById('btn-close-help-modal-footer');

  const closeHelp = () => {
    if (helpModal) {
      helpModal.classList.add('hidden');
      document.body.classList.remove('overflow-hidden');
    }
  };

  if (openHelpBtn && helpModal) {
    openHelpBtn.onclick = () => {
      helpModal.classList.remove('hidden');
      document.body.classList.add('overflow-hidden');
    };
  }
  if (closeHelpBtn) closeHelpBtn.onclick = closeHelp;
  if (closeHelpBtnFooter) closeHelpBtnFooter.onclick = closeHelp;

  // Preview Modal Handlers
  const previewModal = document.getElementById('modal-doc-preview');
  const closePreviewBtn = document.getElementById('btn-close-doc-preview');
  const closePreviewBtnFooter = document.getElementById('btn-close-doc-preview-footer');

  const titleEl = document.getElementById('preview-title');
  const catEl = document.getElementById('preview-category');
  const verEl = document.getElementById('preview-version');
  const descEl = document.getElementById('preview-desc');
  const bodyEl = document.getElementById('preview-body');
  const githubLinkEl = document.getElementById('preview-github-link');

  const closePreview = () => {
    if (previewModal) {
      previewModal.classList.add('hidden');
      document.body.classList.remove('overflow-hidden');
    }
  };

  if (closePreviewBtn) closePreviewBtn.onclick = closePreview;
  if (closePreviewBtnFooter) closePreviewBtnFooter.onclick = closePreview;

  document.querySelectorAll('.btn-preview-doc').forEach(btn => {
    btn.onclick = () => {
      const docId = btn.getAttribute('data-doc-id');
      const doc = DOCS_DATA.find(d => d.id === docId);
      if (!doc || !previewModal) return;

      titleEl.textContent = doc.title;
      catEl.textContent = `${doc.category} • ${doc.type}`;
      verEl.textContent = `v${doc.version}`;
      descEl.textContent = doc.desc;
      bodyEl.innerHTML = doc.contentHtml;
      githubLinkEl.href = `${GITHUB_REPO_URL}/blob/main/Wiki/${doc.file}`;

      previewModal.classList.remove('hidden');
      document.body.classList.add('overflow-hidden');
    };
  });

  // Copy CLI commands
  document.querySelectorAll('.btn-copy-cmd, .btn-copy-quick').forEach(btn => {
    btn.onclick = async () => {
      const cmd = btn.getAttribute('data-cmd');
      if (cmd) {
        try {
          await navigator.clipboard.writeText(cmd);
          showToast('Comando copiado al portapapeles');
        } catch {
          const input = document.createElement('textarea');
          input.value = cmd;
          document.body.appendChild(input);
          input.select();
          document.execCommand('copy');
          input.remove();
          showToast('Comando copiado al portapapeles');
        }
      }
    };
  });
}

